// src/components/business/business-constants.ts
//
// Constantes compartilhadas das telas de Negócios (correção UX v2 — wireframe
// seção 04). Centraliza labels/transições usadas em BusinessesPage,
// BusinessDetailPage e nos tabs/modal de src/components/business/*.

import { ApiError, type BusinessType, type LifecycleStatus } from '@/lib/api';

export const LIFECYCLE_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  setup: 'Setup',
  active: 'Ativo',
  paused: 'Pausado',
  archived: 'Arquivado',
};

/**
 * Transições válidas do lifecycle (state machine do backend
 * business/service.ts). O select de Status nas Configurações só oferece
 * o status atual + alvos válidos — o wireframe mostra os 5 status livres,
 * mas o PATCH /:id/lifecycle rejeita transição inválida.
 */
export const LIFECYCLE_TRANSITIONS: Record<string, LifecycleStatus[]> = {
  draft: ['setup'],
  setup: ['active'],
  active: ['paused', 'archived'],
  paused: ['active', 'archived'],
  archived: [],
};

/**
 * Tipos de negócio — o wireframe (aba Configurações da seção 04) mostra os
 * valores crus no select (`product`/`service`/`content`/`agency`).
 */
export const TYPE_OPTIONS: { value: BusinessType; label: string }[] = [
  { value: 'product', label: 'product' },
  { value: 'service', label: 'service' },
  { value: 'content', label: 'content' },
  { value: 'agency', label: 'agency' },
];

/**
 * Modelo de receita — opções literais do select do wireframe
 * (`assisted`/`autonomous`/`none`). Persistido em dna.revenue_model
 * (PATCH /v1/businesses/:id/dna); leitura com fallback na coluna
 * businesses.revenue_goal ("dna + colunas", C3).
 */
export const REVENUE_GOAL_OPTIONS = ['assisted', 'autonomous', 'none'] as const;

/** Tom do status-dot da Visão geral (wireframe `.dot ok` no mv de Status). */
export const LIFECYCLE_DOT_TONE: Record<string, 'neutral' | 'run' | 'ok' | 'wait' | 'bad'> = {
  draft: 'neutral',
  setup: 'run',
  active: 'ok',
  paused: 'wait',
  archived: 'bad',
};

export const REPO_TYPE_OPTIONS = [
  { value: 'frontend', label: 'frontend' },
  { value: 'backend', label: 'backend' },
  { value: 'mobile', label: 'mobile' },
  { value: 'infra', label: 'infra' },
  { value: 'monorepo', label: 'monorepo' },
];

/** Iniciais do negócio pro tile de logo (ex: "Transcreve ADV" → "TA"). */
export function businessInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * Mensagem de erro do toast da exclusão de negócio (plano §4.3.5): 403/409
 * mostram a MENSAGEM DO BACKEND (ex.: "negócio tem 92 demandas vinculadas"),
 * nunca genérica. Caso especial pré-restart: a rota DELETE /v1/businesses/:id
 * ainda não existe no backend → 404 SEM code `business_not_found` → aviso
 * claro de indisponibilidade (não confundir com negócio inexistente).
 */
export function deleteBusinessErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404 && err.code !== 'business_not_found') {
      return 'Exclusão de negócio indisponível no backend (aguardando restart). Tente novamente mais tarde.';
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Falha ao excluir o negócio. Tente novamente.';
}

/** Demand row com a coluna business_id (presente na API, ausente no type Demand). */
type WithBusinessId = { business_id?: string | null };

/**
 * Filtro defensivo de demandas por negócio (C4, pré-restart): se a row traz a
 * coluna business_id, ela manda; se a coluna não vier no payload (backend novo
 * já filtrou server-side via ?business_id), confiamos no servidor.
 */
export function filterDemandsByBusiness<T extends object>(items: T[], businessId: string): T[] {
  return items.filter((d) => {
    if (!('business_id' in d)) return true;
    return (d as WithBusinessId).business_id === businessId;
  });
}
